import React, { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import POSIconSrc from '~Assets/icons/point_of_sale.svg';
import TSRIconSrc from '~Assets/icons/restaurant.svg';
import SettingsIconSrc from '~Assets/icons/settings.svg';
import EndOfDayIconSrc from '~Assets/icons/MainMenu/calender-check-outlined.svg';
import SafTIconSrc from '~Assets/icons/receipt.svg';
import HistoryIconSrc from '~Assets/icons/history.svg';
import TrophyIconSrc from '~Assets/icons/trophy.svg';
import CryptoCurrencyIconSrc from '~Assets/icons/crypto-currency.svg';
import ZReportIconSrc from '~Assets/icons/article.svg';
import ReportIconSrc from '~Assets/icons/receipt.svg';
import DevicesIconSrc from '~Assets/icons/devices.svg';
import PunchClockIconSrc from '~Assets/icons/MainMenu/punch-clock-outlined.svg';
import MoneyOutlinedSrc from '~Assets/icons/MainMenu/money-outlined.svg';
import { PAGES, usePage } from '~Scenes/Handy/Context/PageProvider';
import { Grid } from '~Scenes/Handy/styles';
import { useI18n, useLicenses, useMetadata, useSelector, useStaffCan } from '~Hooks';
import { STAFF_PERMISSIONS } from '../../../../../__queries__';
import DemoIntro from '../DemoIntro';
import { useTableContext } from '~Scenes/Handy/Context/TableContext';
import { useCompetition } from '~Providers/CompetitionsProvider';
import NavButton from './NavButton';
import { useStorage } from '~Components/Storage';

type ButtonType = {
  title: string;
  icon: JSX.Element;
  onClick: () => void;
  key: string;
  background: string;
  isLicenseConditionMet?: boolean;
  permissions?: STAFF_PERMISSIONS[];
};

const Wrapper = styled.div`
  height: 100%;
  margin: 0;
  padding: 2rem;
  box-sizing: border-box;
  animation: fade-in 1s cubic-bezier(0.215, 0.61, 0.355, 1);
`;

export const GridItem = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: bold;
  text-decoration: none;
  color: #333;
  height: 100%;
  transition: all 0.2s ease-out;
  ${({ disabled }) => disabled && 'opacity: 0.5'}
`;

const Icon = styled.div`
  mask-size: contain;
  mask-repeat: no-repeat;
  height: 42px;
  width: 42px;
`;

const SettingsIcon = styled(Icon)`
  mask-image: url(${SettingsIconSrc});
  background-color: #545960;
`;

const EndOfDayIcon = styled(Icon)`
  mask-image: url(${EndOfDayIconSrc});
  background-color: #e57373;
`;

const SafTIcon = styled(Icon)`
  mask-image: url(${SafTIconSrc});
  background-color: #4a7ebc;
`;

const TSRIcon = styled(Icon)`
  mask-image: url(${TSRIconSrc});
  background-color: #ffa502;
`;

const POSIcon = styled(Icon)`
  mask-image: url(${POSIconSrc});
  background: #1c8c8c;
`;

const HistoryIcon = styled(Icon)`
  mask-image: url(${HistoryIconSrc});
  background-color: #1642be;
`;

const TrophyIcon = styled(Icon)`
  mask-image: url(${TrophyIconSrc});
  background-color: #cd7f32;
`;

const CryptoCurrencyIcon = styled(Icon)`
  mask-image: url(${CryptoCurrencyIconSrc});
  background-color: #b35e00;
`;

const ZReportIcon = styled(Icon)`
  mask-image: url(${ZReportIconSrc});
  background-color: #2c4774;
`;

const ReportIcon = styled(Icon)`
  mask-image: url(${ReportIconSrc});
  background-color: #092453;
`;

const DevicesIcon = styled(Icon)`
  mask-image: url(${DevicesIconSrc});
  background-color: #6366f1;
`;

const PunchClockIcon = styled(Icon)`
  mask-image: url(${PunchClockIconSrc});
  background-color: #059669;
`;

const CashManagementIcon = styled(Icon)`
  mask-image: url(${MoneyOutlinedSrc});
  background-color: #16a34a;
`;

const HandyHome = () => {
  const { i18n } = useI18n();
  const staffCan = useStaffCan();
  const [storage] = useStorage();
  const metadata = useMetadata();

  const page = usePage();

  const flags = useSelector((state) => state.companyFlags);
  const country = useSelector((state) => state.country);
  const { search } = useLocation();
  const history = useHistory();
  const demoMode = useSelector((state) => state.demoMode);
  const { selectedTable, setSelectedTable } = useTableContext();
  const competitions = useCompetition();
  const licenses = useLicenses();

  const paymentMethods = useSelector((state) => state.POSPaymentMethods);
  const isCryptoEnabled = paymentMethods.some((method) => method.id === 'CRYPTO');

  //If there are any search params remove them
  useEffect(() => {
    if (search) {
      history.push({
        search: '',
      });
    }
  }, [search]);

  useEffect(() => {
    if (selectedTable && demoMode) {
      setSelectedTable(null);
    }
  }, [selectedTable]);

  const buttons = useMemo(() => {
    const buttonData = [
      {
        title: 'POS',
        icon: <POSIcon />,
        onClick: () => page.navigateTo('POS'),
        key: 'pos',
        background: 'linear-gradient(150deg, #bdf4ff 0%, #baffff 100%)',
        isLicenseConditionMet: licenses.hasHandheldPro || licenses.isHandheldProClaimable,
      },
      {
        title: i18n('Handy.Tables'),
        icon: <TSRIcon />,
        onClick: () => page.navigateTo('ordering'),
        key: 'tables',
        background: 'linear-gradient(150deg, #e9ffac  0%, #ffffaa 100%)',
      },
      !metadata.data.enableAutoCashEOD && {
        title: i18n('Handy.EndOfDay'),
        icon: <EndOfDayIcon />,
        onClick: () => page.navigateTo('endOfDay'),
        key: 'endOfDay',
        background: 'linear-gradient(150deg, #ffcccc, #ffcccc 100%)',
        permissions: PAGES.endOfDay.permissions,
      },
      {
        title: i18n('Menu.CashManagement'),
        icon: <CashManagementIcon />,
        onClick: () => page.navigateTo('cash-management'),
        key: 'cash-management',
        background: 'linear-gradient(150deg, #e4ffc7 0%, #bdf59a 100%)',
        isLicenseConditionMet: paymentMethods.some((method) => method.id === 'CASH'),
      },
      competitions.hasCompetitions && {
        title: i18n('Competitions.Title'),
        icon: <TrophyIcon />,
        onClick: () => page.navigateTo('competitions'),
        key: 'competitions',
        background: 'linear-gradient(150deg, #e3c19e, #e3c19e 100%)',
      },
      {
        title: 'SAF-T',
        icon: <SafTIcon />,
        onClick: () => page.navigateTo('safT'),
        key: 'safT',
        background: 'linear-gradient(150deg, #99ccff 0%, #99ccff 100%)',
      },
      licenses.hasStaffLedger && {
        title: i18n('StaffShifts.Timeclock'),
        icon: <PunchClockIcon />,
        onClick: () => page.navigateTo('staff-shifts'),
        key: 'staff-ledger',
        background: 'linear-gradient(150deg, #d1f5e6 0%, #a7f0cf 100%)',
      },
      {
        title: i18n('Handy.Page.history'),
        icon: <HistoryIcon />,
        onClick: () => page.navigateTo('history'),
        key: 'history',
        background: 'linear-gradient(150deg, #e6caff 0%, #d0c6ff 100%)',
        permissions: PAGES.history.permissions,
        isLicenseConditionMet:
          licenses.hasHandheldOrder ||
          licenses.hasHandheldPro ||
          licenses.isHandheldOrderClaimable ||
          licenses.isHandheldProClaimable,
      },
      isCryptoEnabled && {
        title: 'Crypto',
        icon: <CryptoCurrencyIcon />,
        onClick: () => page.navigateTo('crypto'),
        key: 'crypto',
        background: 'linear-gradient(150deg, #f7931a 0%, #ffe8b3 100%)',
      },
      {
        title: i18n('Handy.Page.XZ'),
        icon: <ZReportIcon />,
        onClick: () => page.navigateTo('z-reports'),
        key: 'z-reports',
        background: 'linear-gradient(150deg, #708cbc 0%, #9bb2db 100%)',
        permissions: PAGES['z-reports'].permissions,
      },
      {
        title: i18n('Handy.Page.Reports'),
        icon: <ReportIcon />,
        onClick: () => page.navigateTo('reports'),
        key: 'reports',
        background: 'linear-gradient(150deg, #7073bc 0%, #8588ce 100%)',
      },
      {
        title: i18n('Handy.Settings'),
        icon: <SettingsIcon />,
        onClick: () => page.navigateTo('settings'),
        key: 'settings',
        background: 'linear-gradient(150deg, #d5d7d8, #c3c5c7 100%)',
        permissions: PAGES.settings.permissions,
      },
    ].filter(Boolean) as ButtonType[];

    if (flags.isPaymentsOnly) {
      return [
        {
          title: 'Standalone',
          icon: <POSIcon />,
          onClick: () => page.navigateTo('standalone'),
          key: 'standalone',
          background: 'linear-gradient(150deg, #bdf4ff 0%, #baffff 100%)',
        },
        {
          title: i18n('Handy.Settings'),
          icon: <SettingsIcon />,
          onClick: () => page.navigateTo('settings'),
          key: 'settings',
          background: 'linear-gradient(150deg, #d5d7d8, #c3c5c7 100%)',
          permissions: PAGES.settings.permissions,
        },
      ];
    }

    return buttonData;
  }, [
    i18n,
    licenses.hasHandheldPro,
    licenses.hasHandheldOrder,
    metadata.data.enableAutoCashEOD,
    competitions.hasCompetitions,
    isCryptoEnabled,
    flags.isPaymentsOnly,
    storage.adyenTerminals,
    paymentMethods,
  ]);

  if (demoMode) {
    return <DemoIntro />;
  }

  return (
    <Wrapper>
      <Grid>
        {buttons.map(
          ({ title, icon, onClick, key, background, permissions, isLicenseConditionMet }) => {
            let disabled = !!permissions?.length && !staffCan.multi(permissions);

            if (key === 'reports') {
              if (storage.staffCanViewOwnReport || staffCan.REPORTS) {
                disabled = false;
              } else {
                disabled = true;
              }
            }

            // If there are licenses required, check if the company has any of them.
            if (isLicenseConditionMet === false) {
              return null;
            }

            // If manualEndOfDay is disabled, don't show that button
            if (key === 'endOfDay' && !flags.manualEndOfDay) {
              return null;
            }

            // SafT is currently only for Norway compliance
            if (key === 'safT' && country !== 'NO') {
              return null;
            }

            return (
              <NavButton
                key={key}
                title={title}
                icon={icon}
                background={background}
                disabled={disabled}
                onClick={onClick}
              />
            );
          },
        )}
      </Grid>
    </Wrapper>
  );
};

export default HandyHome;
